import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "@emotion/react/jsx-runtime";
import { useUpdateEffect } from '@react-hookz/web';
import { useConfiguration, useBreakpointDown } from '@travel-frontend/core';
import { Trans, useTranslation } from 'next-i18next';
import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { GenderSelect, PasswordContent, PolicyInfo, WidgetLayout } from '../../../components/Authentication/components/index.js';
import { ActivateAccountDialog, VerifyCustomerSuccessDialog } from '../../../components/Authentication/dialogs/index.js';
import { isPasswordValid, validationsConfig } from '../../../components/Authentication/helpers.js';
import { doRegister } from '../../../components/Authentication/redux/actions.js';
import { authState } from '../../../components/Authentication/redux/AuthState.js';
import { useAuthentication, useValidation, useValidationError, useValidationErrorEvaluate } from '../../../hooks/index.js';
import { useTracking } from '../../../hooks/useTracking/useTracking.js';
import { ActionLink, Button, HeadlineLarge, LabelSmall, ParagraphLarge, Stack, TextField } from '../../../primitives/index.js';
import { AUTHENTICATION_QA_ID, qaId } from '../../../utils/qaId.js';
export const Registration = ({
  onFail,
  ...restProps
}) => {
  const {
    t
  } = useTranslation('authentication');
  const smDown = useBreakpointDown('sm');
  const dispatch = useDispatch();
  const authUrl = useConfiguration('authUrl');
  const {
    trackClickEvent
  } = useTracking();
  const {
    validate
  } = useValidation(validationsConfig);
  const {
    errors,
    setFieldErrors,
    resetErrors
  } = useValidationError([]);
  const {
    isComponentOpen,
    toggleWidget
  } = useAuthentication();
  const {
    isFieldValid
  } = useValidationErrorEvaluate(errors);
  const {
    errorType,
    isLoading
  } = useSelector(authState);
  const [passwords, setPasswords] = useState({
    newPassword: '',
    confirmationPassword: ''
  });
  const {
    isValid,
    validations
  } = isPasswordValid(passwords.newPassword, passwords.confirmationPassword);
  const size = smDown ? 'small' : 'medium';
  const [credentials, setCredentials] = useState({
    emailAddress: '',
    firstName: '',
    lastName: '',
    salutation: ''
  });
  useUpdateEffect(() => {
    resetErrors();
  }, [isComponentOpen('widget', 'registration')]);
  const handleFieldChange = useCallback((field, value) => {
    const data = {
      ...credentials,
      [field]: value
    };
    setCredentials(data);
  }, [credentials]);
  const resetData = () => {
    setCredentials({
      emailAddress: '',
      firstName: '',
      lastName: '',
      salutation: ''
    });
    setPasswords({
      newPassword: '',
      confirmationPassword: ''
    });
  };
  const handleButtonClick = useCallback(() => {
    dispatch(
    // @ts-expect-error TS(2345) FIXME
    doRegister(authUrl, {
      ...credentials,
      password: passwords.newPassword
    }, resetData, onFail));
  }, [authUrl, credentials, dispatch, onFail, passwords.newPassword]);
  const inputErrorMessages = field => !isFieldValid(field) && t(`registration.missingCases.${field}`);
  const handleSubmit = event => {
    event.preventDefault();
    let isFormValid = true;
    for (const [field, value] of Object.entries(credentials)) {
      const inputErrors = validate([{
        field,
        value
      }]);
      if (inputErrors.length > 0) {
        isFormValid = false;
        setFieldErrors(inputErrors, field);
      }
    }
    if (isFormValid) {
      handleButtonClick();
    }
  };
  const handleBlur = useCallback(field => () => {
    const data = {
      ...credentials,
      [field]: credentials[field].trim()
    };
    setCredentials(data);
  }, [credentials, setCredentials]);
  const handleFocus = useCallback(field => () => {
    setFieldErrors([], field);
  }, [setFieldErrors]);
  const toggleSignInWidget = useCallback(() => {
    trackClickEvent('login_now_2');
    toggleWidget('signIn');
  }, [toggleWidget, trackClickEvent]);
  const isSubmitDisabled = errors.length > 0 || !isValid || isLoading || !!errorType;
  return _jsxs(_Fragment, {
    children: [_jsx(WidgetLayout, {
      widget: "registration",
      onCloseClick: resetData,
      ...restProps,
      children: _jsxs(Stack, {
        spacing: "large",
        children: [_jsxs(Stack, {
          spacing: "medium",
          children: [_jsx(HeadlineLarge, {
            children: t('registration.title')
          }), _jsx(ParagraphLarge, {
            children: t('registration.subtitle')
          })]
        }), _jsx(LabelSmall, {
          textAlign: "left",
          children: _jsx(Trans, {
            i18nKey: t(`registration.toLoginText`),
            components: [_jsx(ActionLink, {
              onClick: toggleSignInWidget
            }, "signIn")]
          })
        }), _jsx("form", {
          onSubmit: handleSubmit,
          noValidate: true,
          children: _jsxs(Stack, {
            children: [_jsx(GenderSelect, {
              id: "salutation",
              "data-testid": qaId(AUTHENTICATION_QA_ID, 'registrationWidget-gender-select'),
              placeholder: t('registration.salutation'),
              onChange: event => handleFieldChange('salutation', event.value),
              size: smDown ? 'medium' : 'large',
              invalid: !isFieldValid('salutation'),
              message: inputErrorMessages('salutation'),
              onFocus: handleFocus('salutation'),
              isSearchable: false
            }), _jsx(TextField, {
              "data-testid": qaId(AUTHENTICATION_QA_ID, 'registrationWidget-firstName-textfield'),
              id: "first-name",
              autoComplete: "given-name",
              label: t('registration.firstName'),
              onChange: event => handleFieldChange('firstName', event.target.value),
              invalid: !isFieldValid('firstName'),
              message: inputErrorMessages('firstName'),
              size: size,
              onFocus: handleFocus('firstName'),
              onBlur: handleBlur('firstName')
            }), _jsx(TextField, {
              "data-testid": qaId(AUTHENTICATION_QA_ID, 'registrationWidget-lastName-textfield'),
              id: "last-name",
              autoComplete: "family-name",
              label: t('registration.lastName'),
              onChange: event => handleFieldChange('lastName', event.target.value),
              invalid: !isFieldValid('lastName'),
              message: inputErrorMessages('lastName'),
              onFocus: handleFocus('lastName'),
              onBlur: handleBlur('lastName'),
              size: size
            }), _jsx(TextField, {
              "data-testid": qaId(AUTHENTICATION_QA_ID, 'registrationWidget-emailAddress-textfield'),
              id: "email",
              autoComplete: "username",
              type: "email",
              label: t('registration.email'),
              onChange: event => handleFieldChange('emailAddress', event.target.value),
              invalid: !isFieldValid('emailAddress'),
              message: inputErrorMessages('emailAddress'),
              onFocus: handleFocus('emailAddress'),
              onBlur: handleBlur('emailAddress'),
              size: size
            }), _jsx(PasswordContent, {
              label: t('passwordContent.newPassword'),
              onChange: setPasswords,
              testIdType: "registrationWidget",
              checkers: [{
                label: t('passwordContent.validations.eightChars'),
                validation: validations.length
              }, {
                label: t('passwordContent.validations.lowerCase'),
                validation: validations.lowerCase
              }, {
                label: t('passwordContent.validations.upperCase'),
                validation: validations.upperCase
              }, {
                label: t('passwordContent.validations.number'),
                validation: validations.number
              }, {
                label: t('passwordContent.validations.symbol'),
                validation: validations.specialChar
              }, {
                label: t('passwordContent.validations.whiteSpace'),
                validation: validations.whiteSpace
              }],
              confirmationPassword: passwords?.confirmationPassword,
              newPassword: passwords?.newPassword
            }), _jsx(Button, {
              disabled: isSubmitDisabled,
              fullWidth: true,
              type: "submit",
              "data-testid": qaId(AUTHENTICATION_QA_ID, 'registrationWidget-submit-button'),
              children: t('registration.buttonText')
            })]
          })
        }), _jsx(PolicyInfo, {
          onTermsOfUseClick: () => trackClickEvent('terms_of_use_2'),
          onDataProtectionClick: () => trackClickEvent('data_protection_details_2')
        })]
      })
    }), _jsx(ActivateAccountDialog, {}), _jsx(VerifyCustomerSuccessDialog, {})]
  });
};